import { EditIcon } from '@chakra-ui/icons';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Spinner,
  Textarea as ChakraTextarea,
  Tooltip,
  useEditableControls,
  useToast,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import { TEXT_CONSTANT } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { TASK_STATUS } from 'helpers/enum';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockMath, InlineMath } from 'react-katex';
import { useDispatch, useSelector } from 'react-redux';
import { User } from 'types';
import { Message } from 'types/reducers/task';

import TypingText from './TypingText';

interface MessageChatProps {
  data: Message;
  status: TASK_STATUS;
  isLast: boolean;
  user: User;
  cancelEditRef: React.RefObject<HTMLButtonElement>;
}

interface EditableTextareaProps {
  value: string;
  onChange: (value: string) => void;
}

const EditableTextarea = ({ value, onChange: onChangeProp }: EditableTextareaProps) => {
  const { isEditing } = useEditableControls();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '0px';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
      const minHeight = lineHeight + 10;
      textarea.style.height = `${Math.max(minHeight, Math.min(scrollHeight, 500))}px`;
    }
  }, []);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(() => {
        adjustHeight();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, adjustHeight]);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(adjustHeight, 0);
      return () => clearTimeout(timer);
    }
  }, [value, isEditing, adjustHeight]);

  if (!isEditing) {
    return null;
  }

  const lineCount = value ? value.split('\n').length : 1;
  const estimatedHeight = Math.max(40, lineCount * 20 + 10);

  return (
    <ChakraTextarea
      ref={textareaRef}
      sx={{
        height: 'auto',
        minHeight: `${estimatedHeight}px`,
        lineHeight: '1.4',
      }}
      pos='relative'
      zIndex={1}
      overflow='hidden'
      w='100%'
      minH={`${estimatedHeight}px`}
      px={0}
      bg='inherit'
      _focus={{ outline: 'none', boxShadow: 'none' }}
      whiteSpace='pre-line'
      resize='none'
      onChange={(e) => {
        onChangeProp(e.target.value);
        setTimeout(adjustHeight, 0);
      }}
      value={value || ''}
    />
  );
};

const MessageChat = ({ data, status, isLast, user, cancelEditRef }: MessageChatProps) => {
  const toast = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { current, processingStreamingChunkStatusTask } = useSelector((store) => store.task);

  const [isHovering, setIsHovering] = useState(false);
  const [question, setQuestion] = useState('');

  const isError = data?.status === 'error';
  const isShowEditIcon = isHovering && isLast && status !== TASK_STATUS.GENERATE_ANSWER;
  const cleanLatex = (latexString: string): string => {
    return latexString
      .replace(/\\\[\s*\n/g, '\\[')
      .replace(/\n\s*\\\]/g, ' \\]')
      .trim();
  };

  const formatAnswer = (answer: string) => {
    const lines = answer.split('\n');
    let isTable = false;
    let isCodeBlock = false;
    let tableLines: string[] = [];
    let codeBlockLines: string[] = [];
    const bulletListRegex = /^\s*([*+-])\s+/;
    const blockQuoteRegex = /^\s*>/;

    const renderedLines = lines.map((line, lineIndex) => {
      switch (true) {
        case line.trim().startsWith('|') && line.includes('|'):
          isTable = true;
          tableLines.push(line);

          if (lineIndex === lines.length - 1 || !lines[lineIndex + 1].includes('|')) {
            isTable = false;
            const tableContent = tableLines.join('\n');
            tableLines = [];
            return (
              <div key={lineIndex} className='table-wrapper'>
                <Markdown className='table-content' options={{ enforceAtxHeadings: true }}>
                  {tableContent}
                </Markdown>
              </div>
            );
          }
          return null;

        case line.trim().startsWith('```'):
          if (!isCodeBlock) {
            isCodeBlock = true;
          } else {
            isCodeBlock = false;
            const codeBlockContent = codeBlockLines.join('\n');
            codeBlockLines = [];

            const innerRenderedContent = formatAnswer(codeBlockContent);
            return (
              <div key={lineIndex} className='codeblock'>
                <pre>
                  <code>{innerRenderedContent}</code>
                </pre>
              </div>
            );
          }
          return null;

        case isTable:
          tableLines.push(line);
          return null;

        case isCodeBlock:
          codeBlockLines.push(line);
          return null;

        case blockQuoteRegex.test(line.trim()):
          const blockQuoteContent = line.replace(blockQuoteRegex, '').trim();
          return (
            <blockquote key={lineIndex} className='custom-blockquote'>
              {blockQuoteContent}
            </blockquote>
          );

        default:
          if (bulletListRegex.test(line.trim())) {
            const indentation = line.match(/^\s*/)?.[0]?.length || 0;
            const indentationLevel = Math.floor(indentation / 2);

            const bulletMatch = line.trim().match(bulletListRegex);

            if (bulletMatch) {
              const bulletSymbol = bulletMatch[0];

              return (
                <div
                  key={`bullet-${lineIndex}`}
                  style={{ paddingLeft: `${indentationLevel * 20}px` }}
                >
                  <span>{bulletSymbol}</span>
                  {renderMixedContent(line.trim().slice(bulletMatch[0].length))}
                </div>
              );
            }
          }

          return <div key={lineIndex}>{renderMixedContent(line)}</div>;
      }
    });

    return renderedLines;
  };

  const renderMixedContent = (line: string) => {
    const inlineMathPattern = /\\\((.*?)\\\)/g;
    const blockMathPattern = /\\\[(.*?)\\\]/;
    const markdownOptions = {
      enforceAtxHeadings: true, // Only parse headers when # is followed by space
    };

    const elements: JSX.Element[] = [];
    let remaining = line;
    let blockMatch;

    if ((blockMatch = blockMathPattern.exec(remaining)) !== null) {
      const [fullBlockMatch, blockFormula] = blockMatch;

      const precedingText = remaining.slice(0, blockMatch.index);
      if (precedingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{precedingText.trim()}</Markdown>
          </span>
        );
      }

      elements.push(
        <div key={elements.length} style={{ display: 'block', margin: '0' }}>
          <BlockMath>{blockFormula}</BlockMath>
        </div>
      );

      remaining = remaining.slice(blockMatch.index + fullBlockMatch.length);
    }

    let lastIndex = 0;
    let match;
    while ((match = inlineMathPattern.exec(remaining)) !== null) {
      const [fullMatch, formula] = match;

      const precedingText = remaining.slice(lastIndex, match.index);
      if (precedingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{precedingText.trim()}</Markdown>
          </span>
        );
      }

      elements.push(
        <span
          key={elements.length}
          style={{ display: 'inline', marginLeft: '10px', marginRight: '5px' }}
        >
          <InlineMath>{formula.trim()}</InlineMath>
        </span>
      );

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < remaining.length) {
      const remainingText = remaining.slice(lastIndex);
      if (remainingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{remainingText.trim()}</Markdown>
          </span>
        );
      }
    }

    return <>{elements}</>;
  };

  const cleanAnswer = cleanLatex(data.answer ?? '');
  const lastAnswer = data.answer ? (
    <div className='markdown-container'>{formatAnswer(cleanAnswer)}</div>
  ) : (
    <span>{TEXT_CONSTANT.SUMMARY.ERROR_ANSWER}</span>
  );

  const onMouseEnter = () => {
    setIsHovering(true);
  };

  const onMouseLeave = () => {
    setIsHovering(false);
  };

  const onSubmidEditQuestion = (question: string) => {
    if (current.selectedFiles.length === 0) {
      toast({
        title: `Please add files before submit`,
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    if (!question) {
      toast({
        title: `Input must not empty`,
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    dispatch(taskAction.setTimeOutForFinalAnswerTask(true));
    dispatch(taskAction.updateProcessingStreamingChunkStatusTask(true));
    dispatch(taskAction.editQuestion({ question, created_at: new Date().toISOString() }));
  };

  useEffect(() => {
    setQuestion(data?.question);
  }, [data?.question]);

  useEffect(() => {
    if (data.answer) {
      dispatch(taskAction.updateProcessingStreamingChunkStatusTask(false));
      dispatch(taskAction.setTimeOutForFinalAnswerTask(false));
      dispatch(taskAction.clearStreamingAnswerChunksTask());
    }
  }, [data.answer, current.task_id]);

  return (
    <div>
      <Flex justify='flex-end' my={{ base: 3 }} px={{ base: 1 }} pt={{ base: 5 }}>
        <Box pos='relative' w={{ md: '55%' }}>
          <Editable
            key={data?.question}
            isPreviewFocusable={false}
            onCancel={() => {
              setQuestion(data?.question || '');
            }}
            onChange={(value) => setQuestion(value)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onSubmit={onSubmidEditQuestion}
            selectAllOnFocus={false}
            submitOnBlur={false}
            value={question}
          >
            <Box
              pos='relative'
              mb={2}
              px={{ base: 2, md: 5 }}
              py={4}
              pe={10}
              color='#ffffff'
              fontSize={{ base: 'small', sm: 'small', md: 'medium' }}
              bg='main_summary_color'
              opacity={isError ? 0.5 : 1}
              borderRadius='10px 10px 0px 10px'
              shadow='shadow_box'
              whiteSpace='pre-line'
            >
              {isLast ? (
                <>
                  <EditablePreview maxW='100%' whiteSpace='pre-line' />
                  <Box
                    as={EditableInput}
                    sx={{
                      display: 'none !important',
                      visibility: 'hidden !important',
                      position: 'absolute',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                  />
                  <EditableTextarea value={question} onChange={setQuestion} />
                </>
              ) : (
                <>{data?.question}</>
              )}
            </Box>
            <Box fontSize={{ base: 'small' }} textAlign='right'>
              <EditableControls
                isShowEditIcon={isShowEditIcon}
                cancelRef={cancelEditRef}
                value={question}
              />
              <Box as='span' color='red'>
                {isError && 'Error sent'}
              </Box>
              {timeFormat(data?.created_at)}
            </Box>
          </Editable>
        </Box>
        <Flex justify='flex-end' direction='column' ms='12px'>
          <Avatar
            color='grey.50'
            bg='main_summary_color'
            name={user.name?.replace(/\s+/g, ' ') || user.fullName}
            size='sm'
          />
        </Flex>
      </Flex>
      <Flex justify='flex-start'>
        <>
          <Flex justify='flex-end' direction='column' me={3}>
            <Avatar color='#000000' bg='#ffffff' name='Answer' size='sm' />
          </Flex>

          {processingStreamingChunkStatusTask &&
          !data.answer &&
          !current.answerChunks.part_of_answer ? (
            <Flex
              mb={2}
              p={5}
              bg='bg_box'
              borderRadius='10px 10px 10px 0px'
              shadow='shadow_box'
              whiteSpace='pre-line'
            >
              <Spinner me={4} /> {t('common.loading_answer')}
            </Flex>
          ) : (
            <Box w='80%'>
              <Box
                mb={2}
                px={{ base: 2, md: 7 }}
                py={4}
                fontSize={{ base: 'small', sm: 'small', md: 'medium' }}
                textAlign='left'
                bg='bg_box'
                borderRadius='10px 10px 10px 0px'
                shadow='shadow_box'
                whiteSpace='pre-line'
              >
                {!data.answer && current.answerChunks.part_of_answer ? (
                  <>
                    <TypingText />
                  </>
                ) : (
                  lastAnswer
                )}
              </Box>
              <Box fontSize={{ base: 'small' }} textAlign='left'>
                {timeFormat(data?.updated_at)}
              </Box>
            </Box>
          )}
        </>
      </Flex>
    </div>
  );
};

interface EditableControlsProps {
  isShowEditIcon: boolean;
  value: string;
  cancelRef: React.RefObject<HTMLButtonElement>;
}

const EditableControls = ({ isShowEditIcon, cancelRef, value }: EditableControlsProps) => {
  const { t } = useTranslation();
  const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
    useEditableControls();
  return isEditing ? (
    <ButtonGroup justifyContent='end' me='10' size='sm'>
      <Button {...getCancelButtonProps()} ref={cancelRef} borderRadius={6} variant='ghost'>
        {t('common.cancel')}
      </Button>
      <Button {...getSubmitButtonProps()} borderRadius={6} isDisabled={!value} variant='action'>
        {t('common.update')}
      </Button>
    </ButtonGroup>
  ) : (
    <>
      {isShowEditIcon && (
        <Box justifyContent='center'>
          <Tooltip label={t('common.editText')}>
            <Box
              pos='absolute'
              top={2}
              right={3}
              _hover={{ cursor: 'pointer' }}
              {...getEditButtonProps()}
            >
              <EditIcon boxSize={'1.0rem'} color='white' />
            </Box>
          </Tooltip>
        </Box>
      )}
    </>
  );
};

export default MessageChat;
